'use server';

import { createClient, createAdminClient } from '@/lib/supabase/server';
import { revalidatePath } from 'next/cache';
import { generateCheckoutSessionId, generateWalkInProjectId } from '@/lib/idgen';
import { requirePermission } from '@/lib/permissions';
import { notifyStaffChannels } from '@/lib/staffNotify';
import { notifyByPreference } from '@/actions/notifications';
import { isStaffRole } from '@/lib/roles';
import { verifyKioskUnlockToken } from '@/lib/kioskUnlockToken';
import { table25 } from '@/lib/supabase/inventory25';
import { dueDateForStorage } from '@/lib/dates';

/** PinGate unlocks the kiosk without creating a Supabase Auth session — so
 *  createClient()+RLS would reject every insert. Same pattern as
 *  lookupKioskStudent / verifyPinGate: service-role after confirming the actor
 *  holds a fresh unlockToken minted at PIN verify (staff UUID alone is not enough). */
async function kioskAdminClient(
  staffId: string,
  unlockToken: string | null | undefined,
  requireScope: 'checkout' | 'return' | 'any' = 'any'
) {
  const claims = verifyKioskUnlockToken(unlockToken, { staffId, requireScope });
  const admin = await createAdminClient();
  const { data } = await admin.from('profiles').select('id, role').eq('id', claims.staffId).maybeSingle();
  if (!data || !isStaffRole(data.role)) {
    throw new Error('Kiosk must be unlocked by staff before checkout.');
  }
  return admin;
}

export interface CheckoutItem {
  // Exactly one of barcode/itemId identifies a line in practice — V1/V2 legacy items
  // are barcode-only, 2.5-typed items (item_id-keyed, per 2_5_013_transaction_tables_
  // item_id.sql) are itemId-only with barcode usually null (Supply/Set/Folder never
  // have one; a 2.5 Unit might, but selection still goes through itemId). Both are
  // optional here rather than a discriminated union so every existing V1/V2 call site
  // (barcode-only) keeps compiling unchanged.
  barcode?: string | null;
  itemId?: string | null;
  itemName: string;
  qty: number;
  conditionOut?: string;
  // Which Supply variant(s) the student asked for (or null = staff picks at
  // pull time) — see 045_variant_selection.sql. Carried through from the
  // originating request_items row so it survives into the checkout record
  // (Pull Sheet / Checkout Receipt both read it from here, not the request).
  variantSelection?: Record<string, number> | null;
  // Scanned/added by staff during checkout, not part of the original
  // request (confirmed intentional — staff need that flexibility). The
  // receipt separates these out so it's clear what was actually requested
  // versus added at pull time. Meaningless for walk-in checkouts (no
  // "original request" to diff against), always false there.
  addedAtCheckout?: boolean;
  // 2.5 Supply outbound fill (2_5_026). Null for non-consumable lines.
  consumableFullnessOut?: 'full' | 'half' | 'quarter' | null;
  /** Bundle tagging (2_5_021) — Folder/Set/attached-asset kiosk expansions share one. */
  groupId?: string | null;
  groupLabel?: string | null;
  /** Units pulled from the item setback pool (2_5_033). Clamped to 0..qty. */
  setbackQty?: number | null;
}

/** Success or a staff-readable failure. Returns `{ error }` instead of throwing so
 *  production Next.js does not replace the message with a Server Components digest. */
export type CreateCheckoutResult = { id: string; error?: undefined } | { id?: undefined; error: string };

export async function createCheckout(data: {
  studentId: string;
  additionalStudentIds?: string[];
  staffId: string;
  requestId?: string | null;
  projectId?: string | null;  // null = walk-in, generate one
  project: string;
  projectType: string;
  isClassAssignment: boolean;
  notes: string;
  dueDate: string;
  items: CheckoutItem[];
  source?: 'staff' | 'kiosk';
  /** Required when source is 'kiosk' — HMAC token from verifyPinGate. */
  kioskUnlockToken?: string | null;
  /**
   * Bug 1 fix (2026-08-25, direct product decision): when the staff Checkout Verify
   * step scans/confirms fewer units than a request line asked for, `items[].qty` above
   * already carries the reduced/verified quantity (staff/checkout's
   * computeVerifiedSubmission) — that's what checkout_items.qty and the qty_available
   * decrement below are keyed on. This array separately corrects the *originating*
   * request_items row(s) to match, "so the qty is reflected everywhere ... releases the
   * hold" for other students. `declined: true` means zero units were confirmed present
   * (nothing to check out for that line) — flips the line to the existing
   * approved/declined convention (src/actions/requests.ts) instead of a bespoke status,
   * `qty` left untouched for the historical record. `declined: false` means a partial
   * short-count — `qty` is overwritten in place (no separate fulfilled_qty column).
   */
  requestItemAdjustments?: { reqItemId: string; qty: number; declined: boolean }[];
}): Promise<CreateCheckoutResult> {
  try {
    return await createCheckoutInner(data);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Checkout failed. Check your connection and try again.';
    console.error('[createCheckout]', message);
    return { error: message };
  }
}

async function createCheckoutInner(data: {
  studentId: string;
  additionalStudentIds?: string[];
  staffId: string;
  requestId?: string | null;
  projectId?: string | null;
  project: string;
  projectType: string;
  isClassAssignment: boolean;
  notes: string;
  dueDate: string;
  items: CheckoutItem[];
  source?: 'staff' | 'kiosk';
  kioskUnlockToken?: string | null;
  requestItemAdjustments?: { reqItemId: string; qty: number; declined: boolean }[];
}): Promise<CreateCheckoutResult> {
  // Kiosk PinGate has no auth session — authorization there is the unlock-token
  // check inside kioskAdminClient (verifyKioskUnlockToken), not a capability
  // check. The staff-desk path had no server-side check at all before this
  // (2026-08-22 audit finding) — trusted RLS alone for studentId/staffId/qty.
  if (data.source !== 'kiosk') {
    await requirePermission('checkout_checkin');
  }
  const supabase = data.source === 'kiosk'
    ? await kioskAdminClient(data.staffId, data.kioskUnlockToken, 'checkout')
    : await createClient();

  // Must never silently double-book something already on an active checkout — was
  // kiosk-only ("staff-desk path had no server-side check at all"); extended to every
  // source (2026-08-25, direct ask — staff working simultaneous approved requests hit
  // this too, not just students at the kiosk) so Confirm can't succeed against an item
  // someone else already has out, from ANY source. Deliberately not scoped to
  // `data.studentId` any more — the original kiosk-only version only caught a student
  // double-checking-out their OWN active loan; the real risk this closes is two
  // DIFFERENT students/staff racing on the same physical item, so this now checks
  // whether the item is out to ANYONE, not just this checkout's own student.
  {
    const itemIds = data.items.map(i => i.itemId).filter((id): id is string => !!id);
    const barcodes = data.items.map(i => i.barcode).filter((b): b is string => !!b);

    type ConflictCheckout = { id: string; source: string | null; project: string | null; due_date: string | null };
    const conflictMessage = (name: string, co: ConflictCheckout | null | undefined) => {
      const due = co?.due_date ? ` · due ${co.due_date.slice(0, 10)}` : '';
      const label = [co?.project, co?.id].filter(Boolean).join(' · ');
      const where = label || due ? ` (${label}${due})` : '';
      return (co?.source ?? 'staff') === 'kiosk'
        ? `"${name}" is already out on a kiosk loan${where} — return it first before checking it out again.`
        : `"${name}" is already on an active checkout${where} — return it before checking it out again.`;
    };

    if (itemIds.length > 0) {
      const { data: byId } = await supabase
        .from('checkout_items')
        .select('item_name, checkouts!inner(id, status, source, project, due_date)')
        .in('item_id', itemIds)
        .eq('checkouts.status', 'active')
        .is('returned_at', null)
        .limit(1);
      if (byId && byId.length > 0) {
        const row = byId[0] as { item_name: string; checkouts: ConflictCheckout };
        return { error: conflictMessage(row.item_name, row.checkouts) };
      }
    }
    if (barcodes.length > 0) {
      const { data: byBc } = await supabase
        .from('checkout_items')
        .select('item_name, checkouts!inner(id, status, source, project, due_date)')
        .in('barcode', barcodes)
        .eq('checkouts.status', 'active')
        .is('returned_at', null)
        .limit(1);
      if (byBc && byBc.length > 0) {
        const row = byBc[0] as { item_name: string; checkouts: ConflictCheckout };
        return { error: conflictMessage(row.item_name, row.checkouts) };
      }
    }
  }

  // Availability check happens BEFORE any row is written (2026-08-25, direct ask —
  // closes the real overbooking bug: qty_available used to only be checked/decremented
  // AFTER the checkouts/checkout_items rows already existed, so a failed decrement left
  // an orphaned checkout record, and — combined with decrement_qty_available's old
  // floor-clamp-to-zero behavior instead of refusing — two concurrent checkouts against
  // the same low-stock item could both "succeed"). decrement_qty_available (066_
  // decrement_qty_available_atomic_guard.sql) is now a single atomic, conditional
  // UPDATE ... WHERE qty_available >= p_delta — it fails (0 rows touched) instead of
  // clamping, so this loop is the live, race-safe check: whatever's true in the
  // database at this exact instant, not a stale client-side snapshot from page load.
  //
  // If item N fails after items 1..N-1 already succeeded, those already-decremented
  // items are rolled back (re-incremented) before throwing, so a multi-item cart never
  // leaves the database in a half-decremented state with no checkout to show for it.
  const decremented: { itemId?: string | null; barcode?: string | null; qty: number }[] = [];
  async function rollbackDecrements() {
    for (const d of decremented) {
      if (d.itemId) {
        await supabase.rpc('increment_qty_available', { p_item_id: d.itemId, p_delta: d.qty } as never);
      } else if (d.barcode) {
        await supabase.rpc('increment_qty_available', { p_barcode: d.barcode, p_delta: d.qty });
      }
    }
  }
  for (const item of data.items) {
    if (item.itemId) {
      const { data: touched, error } = await supabase.rpc('decrement_qty_available', {
        p_item_id: item.itemId,
        p_delta:   item.qty,
      } as never);
      if (error) { await rollbackDecrements(); throw new Error(`qty decrement failed for item ${item.itemId}: ${error.message}`); }
      if (touched === 0) {
        await rollbackDecrements();
        throw new Error(`"${item.itemName}" doesn't have ${item.qty} available right now — someone else may have just checked it out. Refresh and try again.`);
      }
      decremented.push({ itemId: item.itemId, qty: item.qty });
    } else if (item.barcode) {
      const { data: touched, error } = await supabase.rpc('decrement_qty_available', {
        p_barcode: item.barcode,
        p_delta:   item.qty,
      });
      if (error) { await rollbackDecrements(); throw new Error(`qty decrement failed for ${item.barcode}: ${error.message}`); }
      if (touched === 0) {
        await rollbackDecrements();
        throw new Error(`"${item.itemName}" doesn't have ${item.qty} available right now — someone else may have just checked it out. Refresh and try again.`);
      }
      decremented.push({ barcode: item.barcode, qty: item.qty });
    } else {
      await rollbackDecrements();
      throw new Error(`Cannot checkout "${item.itemName}" — missing item id and barcode`);
    }
  }

  // Resolve project ID — for a checkout created from an approved request, the caller
  // passes the request's own `project_id` (assigned once at approval by
  // markProjectApproved in requests.ts, 065_requests_project_id.sql), so the project's
  // identity stays stable across request → checkout → return instead of getting a
  // fresh number at every step. Walk-ins (no request) have nothing to inherit, so they
  // generate a fresh one here.
  const projectId = data.projectId ?? await generateWalkInProjectId(supabase);

  // Generate session ID: PRJ-001-CHK-003
  const id = await generateCheckoutSessionId(projectId, supabase);

  const { error: coError } = await supabase.from('checkouts').insert({
    id,
    project_id: projectId,
    student_id: data.studentId,
    additional_student_ids: data.additionalStudentIds ?? [],
    staff_id: data.staffId,
    request_id: data.requestId ?? null,
    project: data.project,
    project_type: data.projectType,
    is_class_assignment: data.isClassAssignment,
    notes: data.notes,
    due_date: dueDateForStorage(data.dueDate),
    status: 'active',
    source: data.source ?? 'staff',
  });
  if (coError) { await rollbackDecrements(); throw new Error(coError.message); }

  // item_id cast — database.types.ts hasn't been regenerated since
  // 2_5_013_transaction_tables_item_id.sql added this column (regenerating breaks
  // V1/V2 code not ready for a schema diff yet, same reason table25() exists).
  // Defensive: only persist consumable_fullness_out for fraction-style supplies.
  const fullnessCandidateIds = [
    ...new Set(
      data.items
        .filter((i) => i.itemId && i.consumableFullnessOut)
        .map((i) => i.itemId as string)
    ),
  ];
  const fractionForOut = new Set<string>();
  if (fullnessCandidateIds.length > 0) {
    const itemsTable = await table25('items');
    const { data: rows, error: itemsErr } = await itemsTable
      .select('id, role, consumable_return_style')
      .in('id', fullnessCandidateIds);
    if (itemsErr) throw new Error(itemsErr.message);
    for (const row of (rows ?? []) as { id: string; role: string; consumable_return_style: string | null }[]) {
      if (row.role === 'supply' && row.consumable_return_style === 'fraction') {
        fractionForOut.add(row.id);
      }
    }
  }

  const itemRows = data.items.map(i => {
    const qty = i.qty;
    const rawSetback = Math.floor(Number(i.setbackQty) || 0);
    const setbackQty = Math.max(0, Math.min(qty, rawSetback));
    return {
      checkout_id: id,
      barcode: i.barcode ?? null,
      item_id: i.itemId ?? null,
      item_name: i.itemName,
      qty,
      condition_out: i.conditionOut ?? null,
      variant_selection: i.variantSelection ?? null,
      added_at_checkout: i.addedAtCheckout ?? false,
      consumable_fullness_out:
        i.itemId && fractionForOut.has(i.itemId) ? (i.consumableFullnessOut ?? null) : null,
      group_id: i.groupId ?? null,
      group_label: i.groupLabel ?? null,
      setback_qty: setbackQty,
    };
  });
  const { error: itemError } = await supabase.from('checkout_items').insert(itemRows as never);
  if (itemError) {
    await rollbackDecrements();
    // Orphaned checkouts row with no items would otherwise be left behind — clean it
    // up too, best-effort (if this delete itself fails, the original itemError is
    // still the one surfaced to the caller).
    await supabase.from('checkouts').delete().eq('id', id);
    throw new Error(itemError.message);
  }

  // recordMonitorCheckout() call removed 2026-08-18 — it advanced the "Returned —
  // Monitor" review window against items_v1_legacy.repair_status, which no longer
  // exists (table fully dropped). Was already a V1/V2-only concept that no-op'd for
  // every 2.5 line; see src/lib/repairLog.ts's header for the full removal note.

  // If linked to a request, mark it as checked out
  if (data.requestId) {
    const { error: reqStatusError } = await supabase
      .from('requests')
      .update({ status: 'checked_out' })
      .eq('id', data.requestId);
    if (reqStatusError) throw new Error(`Checkout created but failed to update request status: ${reqStatusError.message}`);
  }

  // Bug 1 fix, point 2 (2026-08-25, direct product decision) — correct the
  // originating request_items row(s) down to what was actually verified/checked out,
  // so the reduction "is reflected everywhere ... releases the hold" instead of only
  // living on checkout_items. The qty_available release itself already happened above
  // (decrement_qty_available ran against the already-reduced item.qty, so un-given
  // units were simply never decremented) — this is purely about request_items staying
  // truthful for the Pull Sheet / Request Queue / Project Log, which all read it.
  if (data.requestId && data.requestItemAdjustments && data.requestItemAdjustments.length > 0) {
    for (const adj of data.requestItemAdjustments) {
      const patch = adj.declined
        ? {
            status: 'declined',
            staff_note: 'Not confirmed present at checkout — declined; qty released back to availability.',
          }
        : { qty: adj.qty };
      const { error: adjError } = await supabase
        .from('request_items')
        .update(patch as never)
        .eq('id', adj.reqItemId)
        .eq('request_id', data.requestId);
      if (adjError) {
        throw new Error(
          `Checkout created but failed to sync request quantity for line ${adj.reqItemId}: ${adjError.message}`
        );
      }
    }
  }

  revalidatePath('/staff/checkout');
  revalidatePath('/staff/dashboard');
  revalidatePath('/staff/inventory');

  const itemCount = data.items.reduce((sum, i) => sum + i.qty, 0);
  await notifyByPreference(
    'notify_checkout_submitted',
    'checkout_submitted',
    `checked out ${itemCount} item${itemCount === 1 ? '' : 's'} for "${data.project}" (${id})`,
    { checkoutId: id }
  );

  return { id };
}

export async function completeCheckin(data: {
  checkoutId: string;
  items: {
    checkoutItemId?: string | null;
    barcode?: string | null;
    itemId?: string | null;
    qty: number;
    conditionIn: string;
    /** Assumed or overridden fill level for a 2.5 Supply line (2_5_026). */
    consumableFullnessIn?: 'full' | 'half' | 'quarter' | 'none' | null;
    /** True when staff set fullness manually at check-in — skips deferred review. */
    consumableOverridden?: boolean;
    /** Optional late tag if setback was missed at checkout (2_5_033). */
    setbackQty?: number | null;
  }[];
  partial?: boolean;
  /** Staff who ran this check-in — receives the T+0 consumable-review nudge. */
  checkedInBy?: string | null;
  /** When set (kiosk), refuse to close a staff-desk checkout. */
  requireSource?: 'staff' | 'kiosk';
  /** Required when requireSource is 'kiosk' — PinGate staff id (no auth session). */
  kioskStaffId?: string | null;
  /** Required when requireSource is 'kiosk' — HMAC token from verifyPinGate. */
  kioskUnlockToken?: string | null;
}) {
  // Kiosk returns have no staff auth cookie — same PinGate / unlock-token pattern
  // as createCheckout({ source: 'kiosk' }). Desk check-in keeps the session client.
  const supabase = data.requireSource === 'kiosk'
    ? await kioskAdminClient(data.kioskStaffId ?? data.checkedInBy ?? '', data.kioskUnlockToken, 'any')
    : await createClient();

  if (data.requireSource) {
    const { data: co, error: coErr } = await supabase
      .from('checkouts')
      .select('source')
      .eq('id', data.checkoutId)
      .maybeSingle();
    if (coErr) throw new Error(coErr.message);
    const source = co?.source ?? 'staff';
    if (source !== data.requireSource) {
      throw new Error(
        data.requireSource === 'kiosk'
          ? 'This item is on a staff checkout — return it at the equipment desk, not the kiosk.'
          : 'Checkout source mismatch.'
      );
    }
  }

  const now = new Date().toISOString();
  const reviewDueAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  const pendingReviewLines: { itemId: string; itemName: string }[] = [];
  const restockLines: { itemId: string; itemName: string }[] = [];

  // Resolve item_id from checkout_items when the client only sent checkoutItemId /
  // barcode. Missing item_id used to fall through to the barcode RPC, which silently
  // no-op'd for barcode-less 2.5 nested members and for 2.5 barcodes not on legacy
  // (William 2026-08-12: MB-T12 / Tokina stuck unavailable after return).
  const { data: checkoutLines, error: linesErr } = await supabase
    .from('checkout_items')
    .select('id, item_id, barcode, qty, returned_at')
    .eq('checkout_id', data.checkoutId);
  if (linesErr) throw new Error(linesErr.message);
  const lineById = new Map((checkoutLines ?? []).map((r) => [r.id, r]));
  const lineByBarcode = new Map(
    (checkoutLines ?? []).filter((r) => r.barcode).map((r) => [r.barcode as string, r])
  );

  // Prefer Inventory 2.5 item_id for qty restore — never rely on V1 barcode-only
  // fallbacks when the live catalog is `items`.
  const barcodesNeedingId = [
    ...new Set(
      data.items
        .filter((i) => !i.itemId && !i.checkoutItemId && i.barcode)
        .map((i) => i.barcode as string)
    ),
  ];
  const barcodeToItemId = new Map<string, string>();
  if (barcodesNeedingId.length > 0) {
    const itemsTable = await table25('items');
    const { data: byBc, error: bcErr } = await itemsTable
      .select('id, barcode')
      .in('barcode', barcodesNeedingId);
    if (bcErr) throw new Error(bcErr.message);
    for (const row of (byBc ?? []) as { id: string; barcode: string | null }[]) {
      if (row.barcode) barcodeToItemId.set(row.barcode, row.id);
    }
  }

  function resolveItemId(item: (typeof data.items)[number]): string | null {
    if (item.itemId) return item.itemId;
    if (item.checkoutItemId) {
      const row = lineById.get(item.checkoutItemId);
      if (row?.item_id) return row.item_id;
    }
    if (item.barcode) {
      const row = lineByBarcode.get(item.barcode);
      if (row?.item_id) return row.item_id;
      return barcodeToItemId.get(item.barcode) ?? null;
    }
    return null;
  }

  // Only fraction-style supplies are consumables. A prior checkout bug wrote
  // consumable_fullness_out on every 2.5 line; never treat those as consumable returns.
  const fullnessIds = [
    ...new Set(
      data.items
        .filter((i) => i.consumableFullnessIn)
        .map((i) => resolveItemId(i))
        .filter((id): id is string => Boolean(id))
    ),
  ];
  const fractionItemIds = new Set<string>();
  if (fullnessIds.length > 0) {
    const itemsTable = await table25('items');
    const { data: rows, error: itemsErr } = await itemsTable
      .select('id, role, consumable_return_style')
      .in('id', fullnessIds);
    if (itemsErr) throw new Error(itemsErr.message);
    for (const row of (rows ?? []) as { id: string; role: string; consumable_return_style: string | null }[]) {
      if (row.role === 'supply' && row.consumable_return_style === 'fraction') {
        fractionItemIds.add(row.id);
      }
    }
  }

  for (const item of data.items) {
    const resolvedItemId = resolveItemId(item);
    const isConsumable =
      !!item.consumableFullnessIn &&
      !!resolvedItemId &&
      fractionItemIds.has(resolvedItemId);
    const overridden = !!item.consumableOverridden;
    const reviewStatus = isConsumable
      ? (overridden ? 'finalized_at_checkin' : 'pending')
      : 'none';

    const setbackPatch =
      item.setbackQty != null
        ? { setback_qty: Math.max(0, Math.floor(Number(item.setbackQty) || 0)) }
        : {};

    let query = supabase
      .from('checkout_items')
      .update({
        condition_in: item.conditionIn,
        returned_at: now,
        ...setbackPatch,
        ...(isConsumable ? {
          consumable_fullness_in: item.consumableFullnessIn,
          consumable_review_status: reviewStatus,
          consumable_overridden_at_checkin: overridden,
          consumable_review_due_at: reviewStatus === 'pending' ? reviewDueAt : null,
          consumable_escalation_level: reviewStatus === 'pending' ? 1 : 0,
        } : {}),
      })
      // .is('returned_at', null) makes this idempotent (2026-08-25, direct ask —
      // "iron out the constraints" pass) — without it, a double-submitted check-in
      // (double-click, retried request) would re-run the qty_available restore below
      // a second time for a line already marked returned, crediting qty_available for
      // a physical unit that was only actually returned once. .select('id') lets the
      // qty-restore below only run when this specific line was genuinely still
      // outstanding, not on a no-op replay.
      .is('returned_at', null)
      .eq('checkout_id', data.checkoutId);

    // Prefer the checkout_items row id — multiple Supply units can share item_id.
    if (item.checkoutItemId) {
      query = query.eq('id', item.checkoutItemId);
    } else if (resolvedItemId) {
      query = query.eq('item_id', resolvedItemId);
    } else if (item.barcode) {
      query = query.eq('barcode', item.barcode);
    } else {
      throw new Error('Cannot return a line with no checkout item id, item id, or barcode');
    }

    const { data: updatedRows, error } = await query.select('id');
    if (error) throw new Error(error.message);
    // Already returned (idempotent replay) — skip the qty-restore/consumable-return
    // logic below entirely for this line, but keep processing the rest of the cart.
    if (!updatedRows || updatedRows.length === 0) continue;

    if (isConsumable && resolvedItemId && item.consumableFullnessIn) {
      const { error: consError } = await supabase.rpc('apply_consumable_return', {
        p_item_id: resolvedItemId,
        p_fullness_in: item.consumableFullnessIn,
      });
      if (consError) throw new Error(`consumable return failed for ${resolvedItemId}: ${consError.message}`);

      if (item.consumableFullnessIn === 'none') {
        restockLines.push({ itemId: resolvedItemId, itemName: item.itemId ?? resolvedItemId });
      }
      if (reviewStatus === 'pending') {
        pendingReviewLines.push({ itemId: resolvedItemId, itemName: item.itemId ?? resolvedItemId });
      }
    } else if (resolvedItemId) {
      const { data: touched, error: qtyError } = await supabase.rpc('increment_qty_available', {
        p_item_id: resolvedItemId,
        p_delta: item.qty,
      } as never);
      if (qtyError) throw new Error(`qty increment failed for item ${resolvedItemId}: ${qtyError.message}`);
      if (touched === 0) {
        throw new Error(`qty increment matched no row for item ${resolvedItemId}`);
      }
    } else if (item.barcode) {
      const { data: touched, error: qtyError } = await supabase.rpc('increment_qty_available', {
        p_barcode: item.barcode,
        p_delta: item.qty,
      });
      if (qtyError) throw new Error(`qty increment failed for ${item.barcode}: ${qtyError.message}`);
      if (touched === 0) {
        throw new Error(`qty increment matched no row for barcode ${item.barcode}`);
      }
    } else {
      throw new Error('Cannot restore qty — line has no item id or barcode');
    }

    if (!item.barcode) continue;
    try {
      const { data: waitlisted } = await supabase
        .from('request_items')
        .select('id, request_id, requests!inner(status, locked_at)')
        .eq('barcode', item.barcode)
        .eq('is_waitlisted', true)
        .is('requests.locked_at', null)
        .eq('requests.status', 'pending');

      for (const wi of waitlisted ?? []) {
        await supabase.from('staff_notifications').insert({
          type: 'waitlist_fulfilled',
          request_id: wi.request_id,
          message: `Waitlisted item returned: ${item.barcode} is now available`,
        });
      }
    } catch {
      // Non-fatal
    }
  }

  if (!data.partial) {
    const { error } = await supabase
      .from('checkouts')
      .update({ returned_at: now, status: 'returned' })
      .eq('id', data.checkoutId);
    if (error) throw new Error(error.message);
  }

  try {
    if (pendingReviewLines.length > 0 && data.checkedInBy) {
      await supabase.from('staff_notifications').insert({
        type: 'consumable_review_needed',
        message: `Consumable review needed for checkout ${data.checkoutId} (${pendingReviewLines.length} line${pendingReviewLines.length === 1 ? '' : 's'} — confirm Full/Half/Quarter/None)`,
        target_profile_id: data.checkedInBy,
        checkout_id: data.checkoutId,
      });
    }
    if (restockLines.length > 0) {
      const { data: team } = await supabase
        .from('profiles')
        .select('id')
        .eq('title', 'Equipment Team')
        .eq('role', 'staff');
      const targets = team ?? [];
      if (targets.length === 0) {
        await supabase.from('staff_notifications').insert({
          type: 'consumable_restock',
          message: `Restock needed: consumable returned as None on ${data.checkoutId}`,
          checkout_id: data.checkoutId,
        });
      } else {
        for (const p of targets) {
          await supabase.from('staff_notifications').insert({
            type: 'consumable_restock',
            message: `Restock needed on ${data.checkoutId} — a consumable hit None`,
            target_profile_id: p.id,
            checkout_id: data.checkoutId,
          });
        }
      }
    }
  } catch (err) {
    console.error('[checkin] consumable notifications failed (non-fatal):', err);
  }

  const checkinCount = data.items.reduce((sum, i) => sum + i.qty, 0);
  await notifyByPreference(
    'notify_checkin_submitted',
    'checkin_submitted',
    `checked in ${checkinCount} item${checkinCount === 1 ? '' : 's'} on ${data.checkoutId}${data.partial ? ' (partial)' : ''}`,
    { checkoutId: data.checkoutId }
  );

  revalidatePath('/staff/checkin');
  revalidatePath('/staff/dashboard');
  revalidatePath('/staff/inventory');
}

/**
 * Deferred consumable review — staff confirm or correct the auto step-down fill
 * level. Inventory was already adjusted at check-in via apply_consumable_return;
 * this only corrects fill/qty when the reviewed level differs, then marks reviewed.
 */
export async function finalizeConsumableReview(data: {
  checkoutItemId: string;
  fullnessIn: 'full' | 'half' | 'quarter' | 'none';
}) {
  await requirePermission('checkout_checkin');
  const supabase = await createClient();
  const { data: row, error: fetchErr } = await supabase
    .from('checkout_items')
    .select('id, item_id, consumable_fullness_in, consumable_review_status')
    .eq('id', data.checkoutItemId)
    .maybeSingle();
  if (fetchErr) throw new Error(fetchErr.message);
  if (!row) throw new Error('Checkout line not found');
  if (row.consumable_review_status !== 'pending') {
    throw new Error('Line is not awaiting consumable review');
  }
  if (!row.item_id) throw new Error('Line has no item_id');

  const oldLevel = row.consumable_fullness_in;
  const newLevel = data.fullnessIn;

  if (oldLevel !== newLevel) {
    // Correct inventory without re-running apply_consumable_return (would double-count).
    const { data: item } = await supabase
      .from('items')
      .select('qty, qty_available')
      .eq('id', row.item_id)
      .maybeSingle();
    const cur = item;

    if (oldLevel === 'none' && newLevel !== 'none') {
      // Was consumed: restore unit to shelf (fill lives on the checkout line only)
      const { error: upErr } = await supabase
        .from('items')
        .update({
          qty: (cur?.qty ?? 0) + 1,
          qty_available: Math.min((cur?.qty ?? 0) + 1, (cur?.qty_available ?? 0) + 1),
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.item_id);
      if (upErr) throw new Error(upErr.message);
    } else if (oldLevel !== 'none' && newLevel === 'none') {
      // Was on shelf: remove + consume
      const { error: upErr } = await supabase
        .from('items')
        .update({
          qty: Math.max(0, (cur?.qty ?? 0) - 1),
          qty_available: Math.max(0, (cur?.qty_available ?? 0) - 1),
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.item_id);
      if (upErr) throw new Error(upErr.message);

      const { data: team } = await supabase
        .from('profiles')
        .select('id')
        .eq('title', 'Equipment Team')
        .eq('role', 'staff');
      for (const p of team ?? []) {
        await supabase.from('staff_notifications').insert({
          type: 'consumable_restock',
          message: `Restock needed — consumable review set None on line ${data.checkoutItemId}`,
          target_profile_id: p.id,
        });
      }
    }
    // Same non-none → non-none: no shelf qty change; fill is only on checkout_items.
  }

  const { error: lineErr } = await supabase
    .from('checkout_items')
    .update({
      consumable_fullness_in: newLevel,
      consumable_review_status: 'reviewed',
      consumable_review_due_at: null,
    })
    .eq('id', data.checkoutItemId);
  if (lineErr) throw new Error(lineErr.message);

  revalidatePath('/staff/checkin');
  revalidatePath('/staff/inventory');
}

/**
 * Clear bogus "pending consumable review" rows left by the bug that stamped
 * consumable_fullness_out on every 2.5 checkout line (cameras, sets, etc.).
 * Only fraction-style supplies belong in the Returns review queue.
 */
export async function clearSpuriousConsumableReviews(): Promise<{ cleared: number }> {
  await requirePermission('checkout_checkin');
  const supabase = await createClient();

  const { data: pending, error } = await supabase
    .from('checkout_items')
    .select('id, item_id')
    .eq('consumable_review_status', 'pending');
  if (error) throw new Error(error.message);
  if (!pending?.length) return { cleared: 0 };

  const itemIds = [...new Set(pending.map((r) => r.item_id).filter(Boolean))] as string[];
  const fractionIds = new Set<string>();
  if (itemIds.length > 0) {
    const itemsTable = await table25('items');
    const { data: rows, error: itemsErr } = await itemsTable
      .select('id, role, consumable_return_style')
      .in('id', itemIds);
    if (itemsErr) throw new Error(itemsErr.message);
    for (const row of (rows ?? []) as { id: string; role: string; consumable_return_style: string | null }[]) {
      if (row.role === 'supply' && row.consumable_return_style === 'fraction') {
        fractionIds.add(row.id);
      }
    }
  }

  const spuriousIds = pending
    .filter((r) => !r.item_id || !fractionIds.has(r.item_id))
    .map((r) => r.id);
  if (spuriousIds.length === 0) return { cleared: 0 };

  const { error: clearErr } = await supabase
    .from('checkout_items')
    .update({
      consumable_review_status: 'none',
      consumable_review_due_at: null,
      consumable_escalation_level: 0,
      // Keep fullness_in if somehow set, but stop treating the line as a review.
      // Clearing fullness_out so a future re-open doesn't re-seed fill UI.
      consumable_fullness_out: null,
      consumable_fullness_in: null,
    })
    .in('id', spuriousIds);
  if (clearErr) throw new Error(clearErr.message);

  revalidatePath('/staff/checkin');
  return { cleared: spuriousIds.length };
}

/**
 * Escalate overdue pending consumable reviews:
 *   level 1 (T+0 check-in staff already notified) → at due_at, notify lab_supervisor (level 2)
 *   level 2 → at due_at, notify Equipment Team title (level 3)
 * Call when opening Check In (best-effort). Email via webhook/Resend when configured.
 */
export async function escalatePendingConsumableReviews(): Promise<{ escalated: number }> {
  await requirePermission('checkout_checkin');
  const supabase = await createClient();
  const now = new Date().toISOString();

  const { data: pending, error } = await supabase
    .from('checkout_items')
    .select('id, checkout_id, item_id, item_name, consumable_escalation_level, consumable_review_due_at')
    .eq('consumable_review_status', 'pending')
    .lte('consumable_review_due_at', now);
  if (error) {
    // Migration 2_5_027 not applied yet — no-op.
    console.warn('[checkin] escalatePendingConsumableReviews skipped:', error.message);
    return { escalated: 0 };
  }

  let escalated = 0;
  for (const row of pending ?? []) {
    const level = row.consumable_escalation_level ?? 1;
    if (level >= 3) continue;

    const nextLevel = level + 1;
    const nextDue = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    const label = row.item_name || row.item_id || row.id;
    const notifyTitle =
      nextLevel === 2
        ? 'Consumable review overdue (24h)'
        : 'Consumable review overdue (48h)';
    const notifyBody = `${label} on checkout ${row.checkout_id}`;

    if (nextLevel === 2) {
      await supabase.from('staff_notifications').insert({
        type: 'consumable_review_escalation',
        message: `Consumable review overdue (24h): ${label} on checkout ${row.checkout_id}`,
        target_role: 'superuser',
        checkout_id: row.checkout_id,
      });

      // profiles has no email column — webhook still gets title/body; Resend skipped without emails.
      await notifyStaffChannels({ title: notifyTitle, body: notifyBody, emails: [] });
    } else if (nextLevel === 3) {
      const { data: team } = await supabase
        .from('profiles')
        .select('id')
        .eq('title', 'Equipment Team')
        .eq('role', 'staff');
      const targets = team ?? [];
      if (targets.length === 0) {
        await supabase.from('staff_notifications').insert({
          type: 'consumable_review_escalation',
          message: `Consumable review overdue (48h) — Equipment Team: ${label} on ${row.checkout_id}`,
          checkout_id: row.checkout_id,
        });
      } else {
        for (const p of targets) {
          await supabase.from('staff_notifications').insert({
            type: 'consumable_review_escalation',
            message: `Consumable review overdue (48h): ${label} on checkout ${row.checkout_id}`,
            target_profile_id: p.id,
            checkout_id: row.checkout_id,
          });
        }
      }
      await notifyStaffChannels({ title: notifyTitle, body: notifyBody });
    }

    const { error: upErr } = await supabase
      .from('checkout_items')
      .update({
        consumable_escalation_level: nextLevel,
        consumable_review_due_at: nextLevel >= 3 ? null : nextDue,
      })
      .eq('id', row.id);
    if (!upErr) escalated += 1;
  }

  return { escalated };
}

/**
 * Student self-report of remaining consumable fill while the item is still out.
 * Only allowed for the checkout owner or an additional student on that checkout,
 * and only when the line already has consumable_fullness_out and is not yet returned.
 * Writes to consumable_student_estimate (does not overwrite staff fullness_in).
 */
export async function setConsumableStudentEstimate(
  checkoutItemId: string,
  estimate: 'full' | 'half' | 'quarter' | 'none'
): Promise<void> {
  const supabase = await createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: line, error: lineErr } = await supabase
    .from('checkout_items')
    .select('id, checkout_id, returned_at, consumable_fullness_out')
    .eq('id', checkoutItemId)
    .single();
  if (lineErr || !line) throw new Error(lineErr?.message ?? 'Checkout item not found');
  if (!line.consumable_fullness_out) {
    throw new Error('This line is not a consumable with outbound fill');
  }
  if (line.returned_at) {
    throw new Error('This item has already been returned');
  }

  const { data: checkout, error: coErr } = await supabase
    .from('checkouts')
    .select('student_id, additional_student_ids')
    .eq('id', line.checkout_id)
    .single();
  if (coErr || !checkout) throw new Error(coErr?.message ?? 'Checkout not found');

  const additional = (checkout.additional_student_ids as string[] | null) ?? [];
  const owns =
    checkout.student_id === user.id || additional.includes(user.id);
  if (!owns) throw new Error('Not allowed to update this checkout');

  const { error: upErr } = await supabase
    .from('checkout_items')
    .update({ consumable_student_estimate: estimate } as never)
    .eq('id', checkoutItemId);
  if (upErr) throw new Error(upErr.message);

  revalidatePath('/student/dashboard');
  revalidatePath('/staff/checkin');
}

// getActiveCheckouts/ActiveCheckoutStudent are kiosk-exclusive (only ever imported
// by src/app/kiosk/page.tsx, for its "Return" tab's active-student list) despite
// living in this shared file — per fulfillment-registry.json's coordination rule for
// checkout.ts, kept clearly separated from the staff-checkout functions above by this
// section comment. Staff-side active-checkout viewing goes through CheckoutContext.tsx
// instead (a different, richer shape used by staff/checkin + the TV Dashboard).
export interface ActiveCheckoutStudent {
  studentId: string;
  name: string;
  checkoutId: string;
  itemCount: number;
  dueDate: string;
  items: { barcode: string | null; itemId: string | null; itemName: string; checkoutId: string }[];
}

export async function getActiveCheckouts(opts?: {
  kioskUnlockToken?: string | null;
  kioskStaffId?: string | null;
}): Promise<ActiveCheckoutStudent[]> {
  // Walk-up kiosk Return tab has no auth session — accept a valid unlock token
  // (checkout or return scope). Desk callers keep the permission gate.
  let supabase;
  if (opts?.kioskUnlockToken) {
    supabase = await kioskAdminClient(opts.kioskStaffId ?? '', opts.kioskUnlockToken, 'any');
  } else {
    await requirePermission('checkout_checkin');
    supabase = await createAdminClient();
  }

  const { data, error } = await supabase
    .from('checkouts')
    .select(`
      id,
      due_date,
      student_id,
      profiles!checkouts_student_id_fkey ( first_name, last_name, vid ),
      checkout_items ( barcode, item_id, item_name, returned_at )
    `)
    .eq('status', 'active')
    .eq('source', 'kiosk')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  const map = new Map<string, ActiveCheckoutStudent>();
  for (const co of data ?? []) {
    const profile = co.profiles as unknown as { first_name: string; last_name: string; vid: string } | null;
    if (!profile) continue;
    const items = ((co.checkout_items as unknown as {
      barcode: string | null;
      item_id: string | null;
      item_name: string;
      returned_at: string | null;
    }[]) ?? [])
      .filter((i) => i.returned_at == null)
      .map(i => ({
        barcode: i.barcode,
        itemId: i.item_id,
        itemName: i.item_name,
        checkoutId: co.id,
      }));
    if (items.length === 0) continue;
    const existing = map.get(co.student_id);
    if (existing) {
      existing.itemCount += items.length;
      existing.items.push(...items);
      if (co.due_date < existing.dueDate) existing.dueDate = co.due_date;
    } else {
      map.set(co.student_id, {
        studentId: co.student_id,
        name: `${profile.first_name} ${profile.last_name}`,
        checkoutId: co.id,
        itemCount: items.length,
        dueDate: co.due_date,
        items,
      });
    }
  }
  return [...map.values()];
}
